/**
 * Kinn Kleanbowl Estimator - Client-Side Calculator
 * Shopify-compatible JavaScript implementation
 */

class KleanbowlEstimator {
    constructor() {
        // Constants from 2025 spreadsheet
        this.constants = {
            soapTabCostPerHour: 0.26,
            waterCostPerHour: 0.5,
            avgElecCostPerKwh: 0.23,
            costOfABowl: 6,
            frameCost: 13.91,
            costPerWipe: 0.04,
            secondsPerWipe: 15,
            costPerKleanbowlRefill: 0.27,
            feedingFeedingsPerDay: 2,
            hydrationFeedingsPerDay: 1,
            feedingTimeToClean: 15, // minutes for spills
            trayCostPer: 0.1
        };
    }

    /**
     * Calculate all estimator values based on user inputs
     * @param {Object} inputs - User form inputs
     * @returns {Object} - Complete calculation results
     */
    calculate(inputs) {
        const {
            hourlyWage,
            occupancy,
            savingsFor,
            stayDays,
            cleaningTime
        } = inputs;

        // Validate inputs
        if (!this.validateInputs(inputs)) {
            throw new Error('Invalid input values');
        }

        // General calculations
        const disinfectantCostPerHour = this.constants.soapTabCostPerHour;
        const frameCostAnnual = Math.round(occupancy * this.constants.frameCost * 100) / 100;

        // Wipe calculations
        const wipeTimeHoursPerDay = (this.constants.secondsPerWipe / 60 / 60) * (occupancy / 2);
        const annualWipeLaborCost = hourlyWage * wipeTimeHoursPerDay * 365;
        const annualWipeCost = (occupancy / 2 / 2) * this.constants.costPerWipe * 365;

        // Frame wash calculations
        const kleanFrameWashDays = stayDays !== 0 ? 365 / stayDays : 0;

        // Total washing cost per hour
        const hourlyWashingCost = hourlyWage + 
            this.constants.soapTabCostPerHour + 
            this.constants.waterCostPerHour + 
            this.constants.avgElecCostPerKwh + 
            disinfectantCostPerHour;

        // Frame cleaning calculations
        const kleanFrameCleaningStaffHoursPerDay = (occupancy * 2.1) / 60;
        const kleanFrameWashingCostPerYear = hourlyWashingCost * kleanFrameCleaningStaffHoursPerDay * kleanFrameWashDays;

        // Feeding calculations
        const feedingResults = this.calculateFeeding({
            hourlyWage,
            occupancy,
            cleaningTime,
            hourlyWashingCost,
            frameCostAnnual,
            kleanFrameWashingCostPerYear,
            annualWipeCost,
            annualWipeLaborCost
        });

        // Hydration calculations
        const hydrationResults = this.calculateHydration({
            hourlyWage,
            occupancy,
            cleaningTime,
            hourlyWashingCost,
            frameCostAnnual,
            kleanFrameWashingCostPerYear,
            annualWipeCost,
            annualWipeLaborCost
        });

        // Klean cleaning hours calculation
        const kleanCleaningHoursPerDay = wipeTimeHoursPerDay / 2 + ((occupancy * 2) / stayDays) / 60;

        return {
            inputs,
            feeding: feedingResults,
            hydration: hydrationResults,
            combined: this.calculateCombined(feedingResults, hydrationResults),
            kleanCleaningHoursPerDay,
            sustainability: this.getSustainabilityMetrics()
        };
    }

    /**
     * Calculate feeding-specific costs and savings
     */
    calculateFeeding(params) {
        const {
            hourlyWage,
            occupancy,
            cleaningTime,
            hourlyWashingCost,
            frameCostAnnual,
            kleanFrameWashingCostPerYear,
            annualWipeCost,
            annualWipeLaborCost
        } = params;

        // Tray calculations
        const feedingSpillsPerDay = 0.3 * occupancy;
        const trayCleaningHours = (feedingSpillsPerDay * this.constants.feedingTimeToClean) / 60;
        const trayPerDay = occupancy * this.constants.feedingFeedingsPerDay;
        const trayCost = Math.round((trayPerDay * 365 * this.constants.trayCostPer) + (trayCleaningHours * hourlyWage * 365));

        // Bowl calculations
        const feedingBowlCleaningStaffHours = occupancy * (cleaningTime * this.constants.feedingFeedingsPerDay) / 60;
        const feedingBowlWashingCostPerDay = hourlyWashingCost * feedingBowlCleaningStaffHours;
        const feedingBowlWashingCostPerYear = feedingBowlWashingCostPerDay * 365;
        const feedingBowlsNeeded = occupancy * this.constants.feedingFeedingsPerDay;
        const feedingBowlCostAnnual = feedingBowlsNeeded * this.constants.costOfABowl;
        const feedingBowlCost = Math.round(feedingBowlCostAnnual + feedingBowlWashingCostPerYear);

        // Kleanbowl calculations
        const feedingWipesCostAnnual = annualWipeCost + annualWipeLaborCost;
        const feedingPetRefillCostAnnual = this.constants.costPerKleanbowlRefill * occupancy * this.constants.feedingFeedingsPerDay * 365;
        const feedingKleanCost = Math.round(frameCostAnnual + kleanFrameWashingCostPerYear + feedingWipesCostAnnual + feedingPetRefillCostAnnual);
        const feedingKleanInvestment = Math.round((occupancy * this.constants.frameCost) + ((occupancy * this.constants.feedingFeedingsPerDay * 60) * this.constants.costPerKleanbowlRefill));

        // Savings calculations
        const traySavingsDollars = trayCost - feedingKleanCost;
        const traySavingsPercent = (1 - Math.round(feedingKleanCost / trayCost * 100) / 100) * 100;
        const traySavingsDaily = traySavingsDollars / 365;
        const trayDays = Math.round(feedingKleanInvestment / traySavingsDaily);

        const feedingBowlSavingsDollars = feedingBowlCost - feedingKleanCost;
        const feedingBowlSavingsPercent = (1 - Math.round(feedingKleanCost / feedingBowlCost * 100) / 100) * 100;
        const feedingBowlSavingsDaily = feedingBowlSavingsDollars / 365;
        const feedingBowlDays = Math.round(feedingKleanInvestment / feedingBowlSavingsDaily);

        return {
            tray: {
                cleaningHours: trayCleaningHours,
                cost: trayCost,
                savingsDollars: traySavingsDollars,
                savingsPercent: traySavingsPercent,
                paybackDays: trayDays
            },
            bowl: {
                cleaningStaffHours: feedingBowlCleaningStaffHours,
                cost: feedingBowlCost,
                savingsDollars: feedingBowlSavingsDollars,
                savingsPercent: feedingBowlSavingsPercent,
                paybackDays: feedingBowlDays
            },
            klean: {
                cost: feedingKleanCost,
                investment: feedingKleanInvestment
            }
        };
    }

    /**
     * Calculate hydration-specific costs and savings
     */
    calculateHydration(params) {
        const {
            hourlyWage,
            occupancy,
            cleaningTime,
            hourlyWashingCost,
            frameCostAnnual,
            kleanFrameWashingCostPerYear,
            annualWipeCost,
            annualWipeLaborCost
        } = params;

        // Bowl calculations
        const hydrationBowlCleaningStaffHours = occupancy * (cleaningTime * this.constants.hydrationFeedingsPerDay) / 60;
        const hydrationBowlWashingCostPerDay = hourlyWashingCost * hydrationBowlCleaningStaffHours;
        const hydrationBowlWashingCostPerYear = hydrationBowlWashingCostPerDay * 365;
        const hydrationBowlsNeeded = occupancy * this.constants.hydrationFeedingsPerDay;
        const hydrationBowlCostAnnual = hydrationBowlsNeeded * this.constants.costOfABowl;
        const hydrationBowlCost = Math.round(hydrationBowlCostAnnual + hydrationBowlWashingCostPerYear);

        // Kleanbowl calculations
        const hydrationWipesCostAnnual = annualWipeCost + annualWipeLaborCost;
        const hydrationPetRefillCostAnnual = this.constants.costPerKleanbowlRefill * occupancy * this.constants.hydrationFeedingsPerDay * 365;
        const hydrationKleanCost = Math.round(frameCostAnnual + kleanFrameWashingCostPerYear + hydrationWipesCostAnnual + hydrationPetRefillCostAnnual);
        const hydrationKleanInvestment = Math.round(occupancy * this.constants.frameCost);

        // Savings calculations
        const hydrationBowlSavingsDollars = hydrationBowlCost - hydrationKleanCost;
        const hydrationBowlSavingsPercent = (1 - Math.round(hydrationKleanCost / hydrationBowlCost * 100) / 100) * 100;
        const hydrationBowlSavingsDaily = hydrationBowlSavingsDollars / 365;
        const hydrationBowlDays = Math.round(hydrationKleanInvestment / hydrationBowlSavingsDaily);

        return {
            bowl: {
                cleaningStaffHours: hydrationBowlCleaningStaffHours,
                cost: hydrationBowlCost,
                savingsDollars: hydrationBowlSavingsDollars,
                savingsPercent: hydrationBowlSavingsPercent,
                paybackDays: hydrationBowlDays
            },
            klean: {
                cost: hydrationKleanCost,
                investment: hydrationKleanInvestment
            }
        };
    }

    /**
     * Calculate combined feeding and hydration results
     */
    calculateCombined(feeding, hydration) {
        return {
            totalKleanCost: feeding.klean.cost + hydration.klean.cost,
            totalInvestment: feeding.klean.investment + hydration.klean.investment,
            totalSavingsScenarioA: feeding.tray.savingsDollars + hydration.bowl.savingsDollars,
            totalSavingsScenarioB: feeding.bowl.savingsDollars + hydration.bowl.savingsDollars,
            totalCostScenarioA: feeding.tray.cost + hydration.bowl.cost,
            totalCostScenarioB: feeding.bowl.cost + hydration.bowl.cost,
            paybackScenarioA: Math.round((feeding.klean.investment + hydration.klean.investment) / ((feeding.tray.savingsDollars + hydration.bowl.savingsDollars) / 365)),
            paybackScenarioB: Math.round((feeding.klean.investment + hydration.klean.investment) / ((feeding.bowl.savingsDollars + hydration.bowl.savingsDollars) / 365))
        };
    }

    /**
     * Get sustainability metrics (static data from original)
     */
    getSustainabilityMetrics() {
        return {
            ghg: { klean: 49131, regular: 83995, reduction: 42 },
            energy: { klean: 36850, regular: 58634, reduction: 37 },
            water: { klean: 705080, regular: 917741, reduction: 23 },
            waste: { klean: -16766, regular: -55, reduction: 30320 }
        };
    }

    /**
     * Validate user inputs
     */
    validateInputs(inputs) {
        const { hourlyWage, occupancy, stayDays } = inputs;
        
        const isPositiveNumber = (value) => {
            return /^[+]?\d*\.?\d+$/.test(value) && parseFloat(value) > 0;
        };

        return isPositiveNumber(hourlyWage) && 
               isPositiveNumber(occupancy) && 
               isPositiveNumber(stayDays);
    }

    /**
     * Format number as currency
     */
    formatCurrency(value) {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    }

    /**
     * Format number with commas
     */
    formatNumber(value, decimals = 2) {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value);
    }
}

// Export for use
window.KleanbowlEstimator = KleanbowlEstimator;